import { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Link from 'next/link';
import Image from 'next/image';

import FavoriteIcon from 'components/FavoriteIcon';
import { formatPrice, formatMileage } from 'components/Srp/SearchResultCard/utils';

import engineIcon from 'public/static/images/engine-icon.svg';
import mileageIcon from 'public/static/images/mileage-icon.svg';
import drivetrainRwdIcon from 'public/static/images/drivetrain-icon-rwd.svg';
import drivetrainFwdIcon from 'public/static/images/drivetrain-icon-fwd.svg';
import drivetrainAwdIcon from 'public/static/images/drivetrain-icon-awd.svg';
import manualTransmissionIcon from 'public/static/images/manual-transmission-icon.svg';
import automaticTransmissionIcon from 'public/static/images/automatic-transmission-icon.svg';
import exteriorColorIcon from 'public/static/images/exterior-color-icon.svg';
import interioColorIcon from 'public/static/images/interior-color-icon.svg';
import fuelTypeGasolineIcon from 'public/static/images/fuel-type-gasoline-icon.svg';
import fuelTypeHybridIcon from 'public/static/images/fuel-type-hybrid-icon.svg';
import fuelTypeElectricIcon from 'public/static/images/fuel-type-electric-icon.svg';
import arrow from 'public/static/images/arrow-right-red.svg';
import mpgIcon from 'public/static/images/mpg-icon.svg';
import closeButton from 'public/static/images/close-pill.svg';
import noImages from 'public/static/images/srp/no-images.png';

import { setScrollToVehicleInfo } from 'redux-implementations/vdpScroll';
import favoriteToggleEvent from 'lib/Tracking/Srp/SearchResultCard/FavoriteToggleEvent';
import quickViewViewMoreEvent from 'lib/Tracking/Srp/QuickViewViewMoreEvent';
import useMonthlyPayment from 'lib/hooks/useMonthlyPayment';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import useHashLocation from 'lib/hooks/useHashLocation';
import useFavorite from 'lib/hooks/useFavorite';
import useEvents from 'lib/hooks/useEvents';

import { VehicleInfo } from 'types/Apis/VehicleApiResponse';
import { styles } from './styles';
import { getVehicleInfoRows, getVehicleRowConfig } from './utils';

export default function QuickView({
  vehicle = {} as VehicleInfo,
  onClose,
}: {
  vehicle: VehicleInfo;
  onClose: () => void;
}): JSX.Element {
  const {
    price,
    mileage,
    transmission,
    driveline,
    mpg,
    make,
    model,
    year,
    trim,
    vin,
    trackingParams,
    exteriorColor,
    interiorColor,
    fuelType,
    engineType,
    photoUrls,
    priceFormatted,
  } = vehicle;
  const { t } = useTranslation();
  const { favorite, isFavorited, unfavorite } = useFavorite(vehicle?.vin);
  const logger = useEvents();
  const [favoriteState, toggleFavoriteState] = useReducer((isFav) => !isFav, isFavorited);
  const { isExtraSmall, isMobile } = useDeviceScreen();
  const monthlyPayment = useMonthlyPayment(price);
  const { values, cleanKeys } = useHashLocation();
  const [vdpUrl, setVdpUrl] = useState('');
  const dispatch = useDispatch();
  const imageUrl = photoUrls?.length ? photoUrls[0] : noImages;

  useEffect(() => {
    if (isFavorited && !favoriteState) {
      toggleFavoriteState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFavorited]);

  useEffect(() => {
    dispatch(setScrollToVehicleInfo(true));
  }, [dispatch]);

  useEffect(() => {
    setVdpUrl(
      `${window.location.origin}${window.location.pathname}#${cleanKeys({
        ...values,
        vin,
      })}`
    );
  }, [cleanKeys, values, vin, isMobile]);

  const onToggleFavorite = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    toggleFavoriteState();
    e.preventDefault();
    e.stopPropagation();
    if (isFavorited) {
      unfavorite();
    } else favorite();

    logger.log(favoriteToggleEvent({ isFavorited, trackingParams }));
  };

  const onLogViewMore = (): Promise<void> => logger.log(quickViewViewMoreEvent({ trackingParams }));

  const drivetrainIcon: Record<string, string> = {
    FWD: drivetrainFwdIcon,
    RWD: drivetrainRwdIcon,
    AWD: drivetrainAwdIcon,
    '4X4': drivetrainAwdIcon,
  };

  const fuelTypeIcon: Record<string, string> = {
    electric: fuelTypeElectricIcon,
    diesel: fuelTypeGasolineIcon,
    gasoline: fuelTypeGasolineIcon,
    hybrid: fuelTypeHybridIcon,
    'hydrogen fuel cell': fuelTypeElectricIcon,
    'natural gas': fuelTypeGasolineIcon,
    'plug-in hybrid': fuelTypeHybridIcon,
  };

  const vehicleInfoRows = getVehicleInfoRows(vehicle, t);
  const vehicleRowConfig = getVehicleRowConfig(isExtraSmall ?? false, vehicleInfoRows);

  return (
    <div className="quick-view-container" data-testid="quick-view-container">
      <div className="header-container">
        <div>
          <button aria-label="close" className="close-button" onClick={onClose}>
            <img src={closeButton} alt="back" />
          </button>
        </div>
        <div className="border" />
        <div className="listing-card-container">
          <div className="listing-card">
            <div className="listing-vehicle-details">
              <div>
                <Image
                  src={imageUrl}
                  alt="vehicle-image"
                  width={isExtraSmall ? 70 : 113}
                  height={68}
                  className="vehicle-image"
                  style={{ borderRadius: '4px', objectFit: 'cover' }}
                />
              </div>
              <div className="vehicle-summary">
                <div className="make-model">
                  {year} {make} {model} {trim}
                </div>
                <div className="price">
                  {priceFormatted}{' '}
                  <span className="est-payment">est. {formatPrice(monthlyPayment)}/mo.</span>
                </div>
              </div>
            </div>
            <FavoriteIcon
              onClick={onToggleFavorite}
              active={favoriteState}
              className="inline-icon"
            />
          </div>
        </div>
      </div>
      <div className="detail-container">
        <div className="key-details">
          <div className="section-header">
            <div className="section-title">{t(`quickView.keyDetails`)}</div>
            <Link passHref href={vdpUrl} legacyBehavior>
              <div className="view-more" onClick={onLogViewMore}>
                {t(`quickView.viewMore`)}
                <img className="view-more-arrow" src={arrow} alt="view-more-arrow" />
              </div>
            </Link>
          </div>
          <div className="detail-row">
            <div className="detail-section">
              <div className="detail-icon">
                <img src={engineIcon} alt="engine-icon" />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.engine`)}</div>
                <div className="detail-content">{engineType}</div>
              </div>
            </div>
            <div className="detail-section">
              <div className="detail-icon">
                <img src={mileageIcon} alt="mileage-icon" />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.mileage`)}</div>
                <div className="detail-content">{formatMileage(mileage)}</div>
              </div>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-section">
              <div className="detail-icon">
                <img src={drivetrainIcon[driveline]} alt="drivetrain-icon" />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.drivetrain`)}</div>
                <div className="detail-content">{driveline}</div>
              </div>
            </div>
            <div className="detail-section">
              <div className="detail-icon">
                <img
                  src={
                    transmission === 'automatic'
                      ? automaticTransmissionIcon
                      : manualTransmissionIcon
                  }
                  alt="transmission-icon"
                />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.transmission`)}</div>
                <div className="detail-content transmission">{transmission}</div>
              </div>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-section">
              <div className="detail-icon">
                <img src={exteriorColorIcon} alt="exterior-color-icon" />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.exteriorColor`)}</div>
                <div className="detail-content">{exteriorColor}</div>
              </div>
            </div>
            <div className="detail-section">
              <div className="detail-icon">
                <img src={mpgIcon} alt="mpg-icon" />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.mpg`)}</div>
                <div className="detail-content">{mpg}</div>
              </div>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-section">
              <div className="detail-icon">
                <img src={interioColorIcon} alt="interior-color-icon" />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.interiorColor`)}</div>
                <div className="detail-content">{interiorColor}</div>
              </div>
            </div>
            <div className="detail-section">
              <div className="detail-icon">
                <img src={fuelTypeIcon[fuelType] ?? fuelTypeGasolineIcon} alt="fuel-type-icon" />
              </div>
              <div>
                <div className="detail-title">{t(`quickView.fuelType`)}</div>
                <div className="detail-content fuel-type">{fuelType}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="vehicle-information">
          <div className="section-header">
            <div className="section-title">{t(`quickView.vehicleInformation`)}</div>
            <Link passHref href={vdpUrl} legacyBehavior>
              <div className="view-more" onClick={onLogViewMore}>
                {t(`quickView.viewMore`)}
                <img className="view-more-arrow" src={arrow} alt="view-more-arrow" />
              </div>
            </Link>
          </div>
          {vehicleRowConfig.map((row) => (
            <div className="information-row" key={row.key}>
              {row.sections.map((section) => (
                <div className="information-section" key={section.title}>
                  <div className="information-title">{section.title}</div>
                  <div className="information-content">{section.content}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <style jsx>{styles}</style>
      <style>{`
        .inline-icon {
          position: relative !important;
          top: auto !important;
          right: auto !important;
        }
        .inline-icon button {
          width: 32px !important;
          height: 32px !important;
        }
        .inline-icon img {
          width: 17.5px;
          height: 16px;
        }
      `}</style>
    </div>
  );
}
